import React, {useCallback} from 'react';

import {TranslatedOfficeLocation} from '@lexian/api/casus-api';
import {useMasterdata} from '_/hooks/useMasterdata';
import i18n from '_/i18n';
import {EFAutocomplete, EFAutocompleteProps} from '_/utilities/form';
import {getActiveOptions, getOptions} from '_/utilities/masterdata.utils';

import {
  useActiveFilters,
  UseActiveFiltersProps,
} from './ActiveFilter/ActiveFilter.hooks';
import {EFTagsAutocomplete} from './ETagsAutocomplete';

type CommonProps = Omit<EFAutocompleteProps, 'options'> & {multiple?: boolean};

type FilterableProps = {filterable: true} & CommonProps &
  Pick<UseActiveFiltersProps, 'onFilterChange' | 'persistenceKey'>;

type NonFilterableProps = {filterable: false} & CommonProps;

type Props = (FilterableProps | NonFilterableProps) & {
  onUserChange?: (
    specialityField: TranslatedOfficeLocation | undefined
  ) => void;
};

export function OfficeLocationAutocomplete({
  label = i18n.t('common:labels.officeLocation'),
  onUserChange,
  multiple = false,
  filterable,
  ...otherProps
}: Props): React.ReactElement {
  const {officeLocations} = useMasterdata();

  const onChangeHandler = useCallback(
    (
      e: React.ChangeEvent<any>,
      originalEventHandler: React.ChangeEventHandler
    ) => {
      if (onUserChange) {
        const selectedOfficeLocation = officeLocations.find(
          (officeLocation) => officeLocation.id === e.target.value
        );
        onUserChange(selectedOfficeLocation);
      }
      originalEventHandler(e);
    },
    [onUserChange, officeLocations]
  );

  if (filterable && 'persistenceKey' in otherProps) {
    return (
      <FilterableOfficeLocationAutocomplete
        {...otherProps}
        customOnChange={onChangeHandler}
        filterable={filterable}
        label={label}
        multiple={multiple}
        officeLocations={officeLocations}
      />
    );
  }

  const options = getActiveOptions(officeLocations);

  return (
    <>
      {multiple ? (
        <EFTagsAutocomplete
          {...otherProps}
          customOnChange={onChangeHandler}
          label={label}
          options={options}
          noOptionFiltering
        />
      ) : (
        <EFAutocomplete
          {...otherProps}
          customOnChange={onChangeHandler}
          label={label}
          options={options}
          noOptionFiltering
        />
      )}
    </>
  );
}

function FilterableOfficeLocationAutocomplete({
  label,
  multiple = false,
  officeLocations,
  onFilterChange,
  persistenceKey,
  ...otherProps
}: FilterableProps & {
  officeLocations: TranslatedOfficeLocation[];
}) {
  const {
    activeOptions,
    showActiveFiltersDialog,
    activeFiltersDialog,
    optionFilterInfo,
  } = useActiveFilters({
    options: officeLocations,
    persistenceKey: `officeLocationAutocomplete.searchFilters.${persistenceKey}`,
    onFilterChange,
  });
  const options = getOptions(activeOptions);
  return (
    <>
      {multiple ? (
        <EFTagsAutocomplete
          {...otherProps}
          label={label}
          optionFilterInfo={optionFilterInfo}
          options={options}
          noOptionFiltering
          onFilterClick={showActiveFiltersDialog}
        />
      ) : (
        <EFAutocomplete
          {...otherProps}
          label={label}
          optionFilterInfo={optionFilterInfo}
          options={options}
          noOptionFiltering
          onFilterClick={showActiveFiltersDialog}
        />
      )}
      {activeFiltersDialog}
    </>
  );
}
